/**
 * platform-totp-card.tsx — Cài đặt cá nhân Platform → Xác thực 2 lớp (TOTP).
 *
 * Khác company (profile-section.tsx): PlatformAdmin BẮT BUỘC TOTP với MỌI tài khoản (Nghị định
 * 356/2025/NĐ-CP, Điều 9.3) — không có nút "Tắt". Chỉ có Bật (lần đầu) + Tạo lại mã dự phòng.
 */
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { Check, Copy, Loader2, ShieldAlert, ShieldCheck } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { platformApi, platformApiError } from '@/lib/platform-client'

interface TotpStatus {
  enabled: boolean
  mandatory: true
}

function BackupCodesGrid({ codes }: { codes: string[] }) {
  const [copied, setCopied] = useState(false)
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        Lưu lại 10 mã dự phòng — mỗi mã dùng được <strong>1 lần</strong>. Mã CHỈ hiện DUY NHẤT lần này.
      </p>
      <div className="grid grid-cols-2 gap-2 rounded-lg border bg-muted p-4 font-mono text-sm">
        {codes.map((c) => <div key={c}>{c}</div>)}
      </div>
      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={() => {
          void navigator.clipboard?.writeText(codes.join('\n')).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 2000)
          })
        }}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        {copied ? 'Đã chép' : 'Chép tất cả'}
      </Button>
    </div>
  )
}

function EnableDialog({ open, onOpenChange, onEnabled }: { open: boolean; onOpenChange: (o: boolean) => void; onEnabled: () => void }) {
  const [qr, setQr] = useState<{ otpauthUri: string; qrDataUrl: string } | null>(null)
  const [loadingQr, setLoadingQr] = useState(false)
  const [code, setCode] = useState('')
  const [confirming, setConfirming] = useState(false)
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null)

  function onShow(o: boolean) {
    if (o && !qr && !loadingQr) {
      setLoadingQr(true)
      platformApi.post('/platform/auth/totp/setup/start').then(({ data }) => setQr(data))
        .catch((err) => toast.error(platformApiError(err)))
        .finally(() => setLoadingQr(false))
    }
    if (!o) {
      setQr(null)
      setCode('')
      setBackupCodes(null)
    }
    onOpenChange(o)
  }

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault()
    setConfirming(true)
    try {
      const { data } = await platformApi.post('/platform/auth/totp/setup/confirm', { code: code.trim() })
      setBackupCodes(data.backupCodes)
      onEnabled()
    } catch (err) {
      toast.error(platformApiError(err))
    } finally {
      setConfirming(false)
    }
  }

  const secret = qr?.otpauthUri.match(/secret=([^&]+)/)?.[1]

  return (
    <Dialog open={open} onOpenChange={onShow}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Bật xác thực 2 lớp</DialogTitle>
          {!backupCodes && <DialogDescription>Quét mã QR bằng app Authenticator rồi nhập mã 6 số.</DialogDescription>}
        </DialogHeader>
        {backupCodes ? (
          <>
            <div className="flex items-center gap-2 text-emerald-600">
              <ShieldCheck className="h-5 w-5" /><p className="text-sm font-medium">Đã bật xác thực 2 lớp</p>
            </div>
            <BackupCodesGrid codes={backupCodes} />
            <DialogFooter><Button type="button" onClick={() => onShow(false)}>Đã lưu — Đóng</Button></DialogFooter>
          </>
        ) : loadingQr || !qr ? (
          <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : (
          <form onSubmit={onConfirm} className="space-y-4">
            <div className="flex flex-col items-center gap-2">
              <img src={qr.qrDataUrl} alt="Mã QR xác thực 2 lớp" className="h-44 w-44 rounded-lg border p-2" />
              {secret && (
                <details className="text-xs text-muted-foreground">
                  <summary className="cursor-pointer">Không quét được? Nhập mã thủ công</summary>
                  <code className="mt-1 block break-all">{secret}</code>
                </details>
              )}
            </div>
            <div>
              <Label htmlFor="pa-totp-code">Mã xác thực</Label>
              <Input
                id="pa-totp-code" className="mt-1" inputMode="numeric" autoComplete="one-time-code"
                placeholder="Mã 6 số" value={code} onChange={(e) => setCode(e.target.value)} maxLength={6} autoFocus required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onShow(false)}>Huỷ</Button>
              <Button type="submit" disabled={confirming || code.trim().length !== 6}>
                {confirming && <Loader2 className="h-4 w-4 animate-spin" />} Xác nhận và bật
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

function RegenerateDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (o: boolean) => void }) {
  const [mode, setMode] = useState<'password' | 'code'>('password')
  const [value, setValue] = useState('')
  const [pending, setPending] = useState(false)
  const [codes, setCodes] = useState<string[] | null>(null)

  function onShow(o: boolean) {
    if (!o) { setValue(''); setMode('password'); setCodes(null) }
    onOpenChange(o)
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setPending(true)
    try {
      const body = mode === 'password' ? { password: value } : { code: value }
      const { data } = await platformApi.post('/platform/auth/totp/backup-codes/regenerate', body)
      setCodes(data.backupCodes)
    } catch (err) {
      toast.error(platformApiError(err))
    } finally {
      setPending(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onShow}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Tạo lại mã dự phòng</DialogTitle>
          {!codes && <DialogDescription>Bộ mã cũ sẽ mất hiệu lực ngay — xác thực lại để tạo bộ mới.</DialogDescription>}
        </DialogHeader>
        {codes ? (
          <>
            <BackupCodesGrid codes={codes} />
            <DialogFooter><Button type="button" onClick={() => onShow(false)}>Đóng</Button></DialogFooter>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="flex gap-2 text-sm">
              <button type="button" onClick={() => { setMode('password'); setValue('') }} className={mode === 'password' ? 'font-medium text-primary underline' : 'text-muted-foreground'}>
                Dùng mật khẩu
              </button>
              <span className="text-muted-foreground">·</span>
              <button type="button" onClick={() => { setMode('code'); setValue('') }} className={mode === 'code' ? 'font-medium text-primary underline' : 'text-muted-foreground'}>
                Dùng mã hiện tại
              </button>
            </div>
            <div>
              <Label htmlFor="pa-reauth">{mode === 'password' ? 'Mật khẩu hiện tại' : 'Mã 6 số hiện tại'}</Label>
              <Input
                id="pa-reauth" className="mt-1" type={mode === 'password' ? 'password' : 'text'}
                value={value} onChange={(e) => setValue(e.target.value)} autoFocus required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onShow(false)}>Huỷ</Button>
              <Button type="submit" disabled={pending || !value}>
                {pending && <Loader2 className="h-4 w-4 animate-spin" />} Tạo bộ mã mới
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function PlatformTotpCard() {
  const [status, setStatus] = useState<TotpStatus | null>(null)
  const [enableOpen, setEnableOpen] = useState(false)
  const [regenOpen, setRegenOpen] = useState(false)

  function reload() {
    platformApi.get<TotpStatus>('/platform/auth/totp/status').then(({ data }) => setStatus(data)).catch(() => undefined)
  }
  useEffect(reload, [])

  return (
    <Card>
      <CardHeader>
        <CardTitle>Xác thực 2 lớp (TOTP)</CardTitle>
        <CardDescription>
          Bắt buộc với mọi tài khoản Platform theo Nghị định 356/2025/NĐ-CP, Điều 9.3 — không thể tắt.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap items-center gap-2">
        {!status ? (
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        ) : status.enabled ? (
          <>
            <Badge variant="success" className="gap-1"><ShieldCheck className="h-3.5 w-3.5" /> Đã bật</Badge>
            <Button type="button" variant="outline" size="sm" onClick={() => setRegenOpen(true)}>Tạo lại mã dự phòng</Button>
          </>
        ) : (
          <>
            <Badge variant="secondary" className="gap-1"><ShieldAlert className="h-3.5 w-3.5" /> Chưa bật</Badge>
            <Button type="button" variant="outline" size="sm" onClick={() => setEnableOpen(true)}>Bật xác thực 2 lớp</Button>
          </>
        )}
      </CardContent>

      <EnableDialog open={enableOpen} onOpenChange={setEnableOpen} onEnabled={reload} />
      <RegenerateDialog open={regenOpen} onOpenChange={setRegenOpen} />
    </Card>
  )
}
